import { useState } from 'react'
import { createPortal } from 'react-dom'
import {
  StockDispatchApi,
  getStatusColor,
  getStatusLabel,
  formatQty,
  formatCurrency,
  formatDate,
  DocumentType,
  getDocumentTypeLabel,
  hasDocumentUploaded,
  getDocumentButtonLabel,
  listDocumentFilesFromDispatch,
  sendStockDispatchToFlyte,
} from '@/services/api/stockDispatch'
import TruckRegistrationTable from './TruckRegistrationTable'

interface DispatchDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  dispatchData?: StockDispatchApi | null
  onDocumentClick?: (dispatchId: string, type: DocumentType) => void
  onAddTruck?: (dispatch: StockDispatchApi) => void
  onTruckDataChanged?: () => void
  onFlyteUpdated?: () => void
}

const DispatchDetailsModal = ({
  isOpen,
  onClose,
  dispatchData,
  onDocumentClick,
  onAddTruck,
  onTruckDataChanged,
  onFlyteUpdated,
}: DispatchDetailsModalProps) => {
  const [flyteSending, setFlyteSending] = useState(false)
  const [flyteError, setFlyteError] = useState<string | null>(null)

  if (!isOpen || !dispatchData) return null

  const statusColors = getStatusColor(dispatchData.status)
  const dispatchNoteText = (() => {
    if (!dispatchData.notes) return null
    try {
      const parsed = JSON.parse(dispatchData.notes)
      if (parsed && typeof parsed === 'object' && typeof parsed.text === 'string' && parsed.text.trim()) {
        return parsed.text
      }
      return null
    } catch {
      return dispatchData.notes
    }
  })()

  const documentTypes: DocumentType[] = [
    'dispatchInstruction',
    'proofOfDelivery',
    'transporterInvoice',
    'dispatchNote',
    'salesInvoice',
  ]

  const renderDocumentRow = (type: DocumentType) => {
    const uploaded = hasDocumentUploaded(dispatchData, type)
    const label = getDocumentButtonLabel(dispatchData, type)
    const files = listDocumentFilesFromDispatch(dispatchData, type)

    return (
      <div key={type} className="pb-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
            {getDocumentTypeLabel(type)}
          </span>
          <button
            onClick={() => onDocumentClick?.(dispatchData.id, type)}
            className="hover:opacity-90 transition-opacity"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '20px',
              color: uploaded ? '#13AB5B' : '#212126',
              backgroundColor: uploaded ? '#E8F7EF' : '#FFFFFF',
              border: uploaded ? '1px solid #13AB5B' : '1px solid #E9E9EA',
              borderRadius: '6px',
              padding: '6px 10px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {uploaded ? label : 'Upload'}
          </button>
        </div>
        {files.length > 1 && (
          <div className="mt-2 space-y-1">
            {files.map((file, index) => (
              <button
                key={file.id}
                onClick={() => onDocumentClick?.(dispatchData.id, type)}
                className="block w-full text-left hover:opacity-80"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#39553A',
                }}
              >
                {file.name || `File ${index + 1}`}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '450px',
          maxWidth: '450px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', lineHeight: '26px', color: '#212126' }}>
            Dispatch Details
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ width: '24px', height: '24px' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" fill="none" />
              <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '24px' }}>
          {/* Details Section */}
          <div className="space-y-4">
            {/* Warehouse */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Warehouse
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '14px', lineHeight: '22px', color: '#39553A' }}>
                {dispatchData.warehouse?.name || '—'}
              </span>
            </div>

            {/* Product */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Product
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                {dispatchData.product || '—'}
              </span>
            </div>

            {/* Lot */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Lot
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                {dispatchData.lotNumber || '—'}
              </span>
            </div>

            {/* Qty Dispatched */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Qty Dispatched
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                {formatQty(dispatchData.qtyDispatched, dispatchData.qtyUnit)}
              </span>
            </div>

            {/* Customer */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Customer
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                {dispatchData.customer || '—'}
              </span>
            </div>

            {/* Destination */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Destination
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                {dispatchData.destination || '—'}
              </span>
            </div>

            {/* Dispatch Date */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Dispatch Date
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                {formatDate(dispatchData.dispatchDate)}
              </span>
            </div>

            {/* Dispatch Costs */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Dispatch Costs (transport)
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                {formatCurrency(dispatchData.dispatchCosts, dispatchData.dispatchCostsCurrency)}
              </span>
            </div>

            {/* Status */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                Status
              </span>
              <span
                className="inline-flex items-center text-xs"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: statusColors.textColor,
                  backgroundColor: statusColors.bgColor,
                  borderRadius: '6px',
                  padding: '4px 12px',
                  whiteSpace: 'nowrap'
                }}
              >
                {getStatusLabel(dispatchData.status)}
              </span>
            </div>
          </div>

          {/* FLYTE Integration */}
          <div className="mt-6 space-y-3 pb-4 border-b border-gray-200">
            <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#212126' }}>
              FLYTE
            </h3>
            <div className="flex justify-between items-center">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>
                Trip ID
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '13px', color: '#212126' }}>
                {dispatchData.flyteTripRef || dispatchData.flyteTripId || '—'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>
                FLYTE Status
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '13px', color: '#212126' }}>
                {dispatchData.flyteStatus || '—'}
              </span>
            </div>
            {!dispatchData.flyteTripId && (
              <button
                type="button"
                disabled={flyteSending}
                onClick={async () => {
                  setFlyteError(null)
                  setFlyteSending(true)
                  try {
                    await sendStockDispatchToFlyte(dispatchData.id)
                    onFlyteUpdated?.()
                  } catch (err: unknown) {
                    const msg =
                      (err as { response?: { data?: { error?: { message?: string }; message?: string } } })
                        ?.response?.data?.error?.message ||
                      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                      (err as Error)?.message ||
                      'Failed to send to FLYTE'
                    setFlyteError(msg)
                  } finally {
                    setFlyteSending(false)
                  }
                }}
                className="w-full py-2 px-4 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#FFFFFF',
                  backgroundColor: '#39553A',
                  border: '1px solid #39553A',
                }}
              >
                {flyteSending ? 'Sending…' : 'Send to FLYTE Load Board'}
              </button>
            )}
            {flyteError && (
              <p style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#C62828' }}>{flyteError}</p>
            )}
            <p style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '12px', lineHeight: '18px', color: '#69697A' }}>
              POD and transporter invoice from FLYTE webhooks are imported automatically into this dispatch.
            </p>
          </div>

          {/* Truck Registration */}
          <div className="mt-6" style={{ marginLeft: '-24px', marginRight: '-24px' }}>
            <TruckRegistrationTable
              stockDispatchId={dispatchData.id}
              details={dispatchData.details}
              onAdd={() => onAddTruck?.(dispatchData)}
              onChanged={() => onTruckDataChanged?.()}
              compact
            />
          </div>

          {/* Documents Section */}
          <div className="mt-6 space-y-4">
            <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#212126', marginBottom: '16px' }}>
              Documents
            </h3>
            {documentTypes.map((type) => renderDocumentRow(type))}
          </div>

          {/* Notes Section */}
          {dispatchNoteText && (
            <div className="mt-6">
              <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#212126', marginBottom: '8px' }}>
                Notes
              </h3>
              <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                {dispatchNoteText}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div 
          className="flex-shrink-0 border-t border-gray-200"
          style={{
            padding: '24px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2 px-4 rounded-lg hover:opacity-90 transition-opacity"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '22px',
                color: '#212126',
                backgroundColor: '#FFFFFF',
                border: '1px solid #212126'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default DispatchDetailsModal
